import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { http, API, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, CheckCircle2, ShieldCheck, FileDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";

export default function ConsentPage() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, data: null, err: "" });
  const [signature, setSignature] = useState("");
  const [agrees, setAgrees] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetch(`${API}/consent/${token}`)
      .then(async (r) => {
        const j = await r.json();
        if (!r.ok) throw new Error(j.detail || "Not found");
        return j;
      })
      .then((d) => {
        setState({ loading: false, data: d, err: "" });
        setAgrees(new Array(d.statements.length).fill(false));
      })
      .catch((e) => setState({ loading: false, data: null, err: e.message }));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (!agrees.every(Boolean)) { toast.error("Please acknowledge every statement"); return; }
    if (!signature.trim()) { toast.error("Please type your name to sign"); return; }
    setSubmitting(true);
    try {
      await http.post(`/consent/${token}/sign`, { signature_text: signature, agreements: agrees });
      toast.success("Signed — thank you");
      // reload
      const r = await fetch(`${API}/consent/${token}`);
      setState({ loading: false, data: await r.json(), err: "" });
    } catch (e2) {
      toast.error(formatApiError(e2.response?.data?.detail) || "Failed to sign");
    } finally {
      setSubmitting(false);
    }
  };

  if (state.loading) return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading…</div>;
  if (!state.data) return (
    <div className="min-h-screen flex items-center justify-center p-6 text-center">
      <div>
        <div className="font-display font-bold text-xl">Consent form unavailable</div>
        <p className="text-slate-500 mt-2 text-sm">{state.err || "The link may have expired."}</p>
      </div>
    </div>
  );

  const d = state.data;
  const signed = d.status === "signed";
  const revoked = d.status === "revoked";

  return (
    <div className="min-h-screen bg-slate-50 py-10 px-4 relative z-10">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center gap-2 mb-4">
          <img src="/icon-192.png" alt="Discrimina" className="w-9 h-9 rounded-xl" />
          <div className="font-display font-extrabold text-lg">Discrimina</div>
        </div>
        <div className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
          <div className="text-xs font-bold uppercase tracking-[0.14em] text-slate-500 mb-2">Parental Consent</div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">
            Consent for {d.learner_first_name}'s ABA services
          </h1>
          <p className="mt-3 text-slate-600 text-sm">
            Please read each statement carefully. You must acknowledge every item, then type your full name to sign.
          </p>

          <div className="mt-5 text-sm text-slate-600 border-t border-b border-slate-200 py-4 space-y-1">
            <div><span className="text-slate-500">Clinician:</span> <b>{d.clinician_name}</b> — {d.clinician_email}</div>
            <div><span className="text-slate-500">Parent/Guardian:</span> <b>{d.parent_name}</b> ({d.relation})</div>
            <div><span className="text-slate-500">Form version:</span> {d.form_version}</div>
          </div>

          {revoked && (
            <div className="mt-5 bg-rose-50 border border-rose-200 text-rose-800 rounded-xl p-4 text-sm">
              This consent has been revoked and is no longer valid.
            </div>
          )}

          {signed && !revoked && (
            <div className="mt-5 bg-emerald-50 border border-emerald-200 rounded-xl p-4">
              <div className="flex items-center gap-2 text-emerald-800">
                <CheckCircle2 className="w-5 h-5" />
                <div className="font-display font-bold">Consent signed</div>
              </div>
              <div className="text-sm text-emerald-800 mt-1">
                Signed by <b>{d.signature_text}</b> on {new Date(d.signed_at).toLocaleString()}.
              </div>
              <a
                href={`${API}/consents/${d.id}/pdf?token=${token}`}
                target="_blank" rel="noreferrer"
                data-testid="consent-pdf-link"
                className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-800 hover:text-emerald-900"
              >
                <FileDown className="w-4 h-4" /> Download signed copy (PDF)
              </a>
            </div>
          )}

          {!signed && !revoked && (
            <form onSubmit={submit} className="mt-6 space-y-4">
              <ol className="space-y-3">
                {d.statements.map((s, i) => (
                  <li key={`${i}-${s.slice(0, 40)}`} className="flex items-start gap-3">
                    <Checkbox
                      checked={!!agrees[i]}
                      onCheckedChange={(v) => setAgrees((a) => a.map((x, j) => (j === i ? !!v : x)))}
                      data-testid={`consent-agree-${i}`}
                      className="mt-1"
                    />
                    <span className="text-sm text-slate-700 leading-relaxed">
                      <b className="text-slate-500 mr-1">{i + 1}.</b>{s}
                    </span>
                  </li>
                ))}
              </ol>

              <div className="pt-2">
                <Label htmlFor="sig" className="text-xs font-bold uppercase tracking-wider text-slate-500">
                  Signature — type "{d.parent_name}" exactly
                </Label>
                <Input
                  id="sig" value={signature}
                  data-testid="consent-signature-input"
                  onChange={(e) => setSignature(e.target.value)}
                  placeholder={d.parent_name}
                  className="mt-2 h-12 rounded-xl font-display text-lg"
                />
              </div>

              <Button type="submit" disabled={submitting}
                className="w-full h-12 rounded-full font-semibold"
                data-testid="consent-sign-button">
                <ShieldCheck className="w-4 h-4 mr-2" />
                {submitting ? "Signing…" : "Sign consent"}
              </Button>
              <p className="text-[11px] text-slate-500 text-center">
                Your IP address and timestamp are recorded as part of your electronic signature.
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
